import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Plus, Edit, Trash2, MapPin, Settings, ToggleLeft, ToggleRight, X
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function ExploitationSiteSettings() {
  const { api } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', city: '', site_id: '', active: true });

  const fetchSites = useCallback(async () => {
    try {
      const res = await api.get('/exploitation/sites');
      setSites(res.data);
    } catch {} finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const resetForm = () => { setForm({ name: '', city: '', site_id: '', active: true }); setEditing(null); setShowForm(false); };

  const handleSave = async () => {
    if (!form.name || !form.city || !form.site_id) { toast.error("Tous les champs sont requis"); return; }
    try {
      if (editing) {
        await api.put(`/exploitation/sites/${editing}`, form);
        toast.success('Site modifié');
      } else {
        await api.post('/exploitation/sites', form);
        toast.success('Site ajouté');
      }
      resetForm();
      fetchSites();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleToggle = async (site) => {
    await api.put(`/exploitation/sites/${site.id}`, { ...site, active: !site.active });
    fetchSites();
  };

  const handleDelete = async (site) => {
    if (!window.confirm(`Supprimer "${site.name}" ? Toutes les données associées seront masquées.`)) return;
    await api.delete(`/exploitation/sites/${site.id}`);
    toast.success('Site supprimé');
    fetchSites();
  };

  const handleEdit = (site) => {
    setForm({ name: site.name, city: site.city, site_id: site.site_id, active: site.active });
    setEditing(site.id);
    setShowForm(true);
  };

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[50vh]"><div className="w-8 h-8 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exploitation-site-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Paramètres sites</h1>
          <p className="text-[#8C8F94] text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>Gérer les sites d'exploitation</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full text-sm transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]" data-testid="add-site-btn">
          <Plus size={16} /> Ajouter un site
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sites-list">
        {sites.map(site => (
          <div key={site.id} className={`bg-[#111111] rounded-xl border p-5 transition-all ${site.active ? 'border-[#222] hover:border-[#00E01A]/30' : 'border-[#222] opacity-60'}`} data-testid={`site-settings-${site.site_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${site.active ? 'bg-[#00E01A]/10' : 'bg-[#2C3338]'}`}>
                  <MapPin size={20} className={site.active ? 'text-[#00E01A]' : 'text-[#8C8F94]'} />
                </div>
                <div>
                  <h3 className="text-white font-semibold">{site.name}</h3>
                  <p className="text-[#8C8F94] text-xs">{site.city}</p>
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${site.active ? 'bg-[#142915] text-[#00E01A]' : 'bg-[#2C3338] text-[#8C8F94]'}`}>
                {site.active ? 'Actif' : 'Inactif'}
              </span>
            </div>
            <div className="bg-[#0a0a0a] rounded-lg p-3 mb-3">
              <p className="text-[#8C8F94] text-xs mb-0.5" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>SITE ID</p>
              <p className="text-white font-mono text-sm">{site.site_id}</p>
            </div>
            <div className="flex items-center justify-between">
              <button onClick={() => handleToggle(site)} className="flex items-center gap-2 text-sm text-[#8C8F94] hover:text-white transition-colors" data-testid={`toggle-${site.site_id}`}>
                {site.active ? <ToggleRight size={20} className="text-[#00E01A]" /> : <ToggleLeft size={20} />}
                {site.active ? 'Désactiver' : 'Activer'}
              </button>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(site)} className="p-2 text-[#8C8F94] hover:text-white hover:bg-[#1D2327] rounded-lg transition-colors" data-testid={`edit-site-${site.site_id}`}>
                  <Edit size={14} />
                </button>
                <button onClick={() => handleDelete(site)} className="p-2 text-[#8C8F94] hover:text-[#EF4444] hover:bg-[#2A1515] rounded-lg transition-colors" data-testid={`delete-site-${site.site_id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{editing ? 'Modifier le site' : 'Nouveau site'}</DialogTitle>
            <DialogDescription className="text-[#8C8F94] text-sm">
              {editing ? 'Modifier les informations du site' : 'Ajouter un nouveau site d\'exploitation'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nom du site *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Riding Watt Nice" data-testid="site-form-name" /></div>
            <div><Label>Ville *</Label><Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} placeholder="Nice" data-testid="site-form-city" /></div>
            <div><Label>Site ID *</Label><Input value={form.site_id} onChange={e => setForm(p => ({...p, site_id: e.target.value}))} placeholder="site-nice" data-testid="site-form-id" /><p className="text-[#52555A] text-xs mt-1">Doit correspondre exactement au site_id de l'application booking</p></div>
            <Button className="w-full bg-[#00E01A] hover:bg-[#00B815] text-black" onClick={handleSave} data-testid="save-site-btn">
              {editing ? 'Modifier' : 'Ajouter le site'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
